import clsx from "clsx";

import { CouponPill } from "features/subscriptions/components";
import type { InvoiceEstimate } from "features/subscriptions/types";
import {
  formatPrice,
  formatPricingPeriodInfo,
  getCouponDiscountString,
  parseEstimate,
} from "features/subscriptions/utils";

import Skeleton from "common/components/ui/Skeleton";
import type { PricingPeriodInfo } from "common/types";

interface PriceProps {
  estimate: InvoiceEstimate;
  pricingPeriodInfo: PricingPeriodInfo;
  isScheduledCancellation?: boolean;
  isLoading: boolean;
}

export default function Price({
  estimate,
  pricingPeriodInfo,
  isScheduledCancellation,
  isLoading,
}: PriceProps) {
  let priceValue = 0;
  let previousPriceValue: number;
  let coupon: ReturnType<typeof parseEstimate>["coupon"];
  const priceSubtext = formatPricingPeriodInfo(pricingPeriodInfo);

  if (estimate) {
    const { amountDue, ...rest } = parseEstimate(estimate);

    priceValue = amountDue;

    if (rest.coupon) {
      previousPriceValue = amountDue + rest.coupon.amount;
    }
    coupon = rest.coupon;
  }

  return (
    <div className="flex flex-col gap-1 items-end">
      {isLoading ? (
        <>
          <Skeleton className="h-8 w-20 lg:h-7" />
          <Skeleton className="h-5 w-14 lg:h-4" />
        </>
      ) : (
        <div
          className={clsx([
            isScheduledCancellation &&
              "p-1 px-1.5 bg-black-100 gap-0 items-end flex flex-col rounded-lg",
          ])}
        >
          {coupon && (
            <CouponPill className="absolute -top-3.5 lg:-top-10">
              {coupon.name}
            </CouponPill>
          )}

          <div className="flex gap-x-1 items-center">
            {coupon && (
              <span className="text-end text-yellow-500 text-button-xs">
                {getCouponDiscountString(coupon)}
              </span>
            )}

            <span
              className={clsx([
                isScheduledCancellation
                  ? "text-xs-medium line-through text-black-400"
                  : "text-2xl-bold lg:text-lg-bold",
              ])}
            >
              {formatPrice(priceValue)}
            </span>

            {!!previousPriceValue && (
              <span className="text-2xl-medium lg:text-lg-medium text-black-400 line-through">
                {formatPrice(previousPriceValue)}
              </span>
            )}
          </div>

          <span
            className={clsx([
              isScheduledCancellation
                ? "text-button-xxs text-black-400"
                : "text-sm-medium lg:text-xs-medium text-black-500",
            ])}
          >
            / {priceSubtext}
          </span>
        </div>
      )}
    </div>
  );
}
